import { ArrowRight, Wand2 } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { resizeDoc, type GalleryCategory, type GalleryEntry } from '@bannermatic/core';
import { CodeBlock } from '../components/ui';
import { DocPreview, useBuiltinFonts } from '../components/DocPreview';
import { useSession } from '../lib/session';
import { useMarketingCopy } from './content';
import { Breadcrumbs, MarketingShell } from './MarketingLayout';
import {
  alternateSizes,
  categoryPath,
  PUBLIC_CATEGORIES,
  TEMPLATES_PATH,
  templateApiBody,
  templateCsv,
  templateDoc,
  templateFields,
  templatePath,
  templatesIn,
  templateName,
} from './programmatic';
import { useLocalePath, usePageLocale } from './PublicRoute';

/** Fills {name}-style placeholders. */
export function fill(text: string, vars: Record<string, string | number>): string {
  return text.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
}

/** Where "Use this template" leads: the editor with a copy, through sign-up for visitors. */
export function useTemplateHref(entry: GalleryEntry): string {
  const user = useSession((s) => s.user);
  const target = `/app/templates?use=${encodeURIComponent(entry.id)}`;
  return user ? target : `/signup?next=${encodeURIComponent(target)}`;
}

function TemplateCard({ entry, box = 220 }: { entry: GalleryEntry; box?: number }) {
  const locale = usePageLocale();
  const lp = useLocalePath();
  const doc = useMemo(() => templateDoc(entry, locale), [entry, locale]);
  return (
    <Link
      to={lp(templatePath(entry))}
      className="group flex flex-col items-center gap-3 rounded-2xl bg-zinc-50 p-4 ring-1 ring-zinc-200 transition hover:-translate-y-0.5 hover:bg-white hover:shadow-lg hover:ring-indigo-200"
    >
      <div
        className="flex items-center justify-center overflow-hidden rounded-lg"
        style={{ width: box, height: box }}
      >
        <div className="overflow-hidden rounded-lg shadow-md ring-1 ring-black/5">
          <DocPreview doc={doc} box={box} />
        </div>
      </div>
      <div className="text-center">
        <div className="font-semibold text-zinc-900 group-hover:text-brand-700">
          {templateName(entry, locale)}
        </div>
        <div className="font-mono text-xs text-zinc-500">
          {entry.width} × {entry.height}
        </div>
      </div>
    </Link>
  );
}

function PageIntro({
  crumbs,
  eyebrow,
  h1,
  lead,
}: {
  crumbs: Array<{ label: string; to?: string }>;
  eyebrow: string;
  h1: string;
  lead: string;
}) {
  return (
    <>
      <Breadcrumbs items={crumbs} />
      <div className="mt-10 inline-flex rounded-full bg-violet-100 px-3 py-1 text-xs font-semibold text-violet-700">
        {eyebrow}
      </div>
      <h1 className="mt-4 text-4xl font-extrabold tracking-tight text-zinc-900 md:text-5xl">
        {h1}
      </h1>
      <p className="mt-5 max-w-3xl text-lg text-zinc-600">{lead}</p>
    </>
  );
}

/** All public template categories, one preview each, with links to every template. */
export function TemplatesHubPage() {
  const locale = usePageLocale();
  const copy = useMarketingCopy(locale);
  const g = copy.gallery;
  const lp = useLocalePath();
  useBuiltinFonts();
  return (
    <MarketingShell>
      <section className="mx-auto max-w-6xl px-6 pt-8 pb-24">
        <PageIntro
          crumbs={[{ label: copy.ui.home, to: lp('/') }, { label: g.hub.label }]}
          eyebrow={g.hub.eyebrow}
          h1={g.hub.h1}
          lead={g.hub.lead}
        />
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {PUBLIC_CATEGORIES.map((c) => {
            const items = templatesIn(c);
            return (
              <section key={c} className="flex flex-col rounded-3xl p-5 ring-1 ring-zinc-200">
                <TemplateCard entry={items[0]} box={200} />
                <h2 className="mt-4 text-lg font-bold text-zinc-900">
                  <Link to={lp(categoryPath(c))} className="hover:text-brand-700">
                    {g.categories[c].name}
                  </Link>{' '}
                  <span className="text-sm font-normal text-zinc-400">
                    {fill(g.count, { n: items.length })}
                  </span>
                </h2>
                <ul className="mt-2 flex-1 space-y-1 text-sm">
                  {items.map((e) => (
                    <li key={e.id}>
                      <Link to={lp(templatePath(e))} className="text-zinc-600 hover:text-brand-700">
                        {templateName(e, locale)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      </section>
    </MarketingShell>
  );
}

/** Templates of one category. */
export function TemplateCategoryPage({ category }: { category: GalleryCategory }) {
  const locale = usePageLocale();
  const copy = useMarketingCopy(locale);
  const g = copy.gallery;
  const cat = g.categories[category];
  const lp = useLocalePath();
  useBuiltinFonts();
  return (
    <MarketingShell>
      <section className="mx-auto max-w-6xl px-6 pt-8 pb-24">
        <PageIntro
          crumbs={[
            { label: copy.ui.home, to: lp('/') },
            { label: g.hub.label, to: lp(TEMPLATES_PATH) },
            { label: cat.name },
          ]}
          eyebrow={fill(g.count, { n: templatesIn(category).length })}
          h1={cat.h1}
          lead={cat.lead}
        />
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {templatesIn(category).map((e) => (
            <TemplateCard key={e.id} entry={e} box={260} />
          ))}
        </div>
        <nav className="mt-16 flex flex-wrap gap-2">
          {PUBLIC_CATEGORIES.filter((c) => c !== category).map((c) => (
            <Link
              key={c}
              to={lp(categoryPath(c))}
              className="rounded-full px-3 py-1.5 text-sm text-zinc-600 ring-1 ring-zinc-200 hover:bg-zinc-50 hover:text-brand-700"
            >
              {g.categories[c].name}
            </Link>
          ))}
        </nav>
      </section>
    </MarketingShell>
  );
}

/** One template: big preview, other sizes, the fields data fills, CSV and API examples. */
export function TemplatePage({ entry }: { entry: GalleryEntry }) {
  const locale = usePageLocale();
  const copy = useMarketingCopy(locale);
  const g = copy.gallery;
  const t = g.template;
  const lp = useLocalePath();
  const useHref = useTemplateHref(entry);
  useBuiltinFonts();
  const doc = useMemo(() => templateDoc(entry, locale), [entry, locale]);
  const sizes = useMemo(
    () => alternateSizes(entry).map(([w, h]) => resizeDoc(doc, w, h)),
    [entry, doc],
  );
  const fields = useMemo(() => templateFields(doc), [doc]);
  const vars = {
    name: templateName(entry, locale),
    category: g.categories[entry.category].name,
    size: `${entry.width} × ${entry.height}`,
  };
  const more = templatesIn(entry.category)
    .filter((e) => e.id !== entry.id)
    .slice(0, 3);

  return (
    <MarketingShell>
      <section className="mx-auto max-w-6xl px-6 pt-8 pb-24">
        <Breadcrumbs
          items={[
            { label: copy.ui.home, to: lp('/') },
            { label: g.hub.label, to: lp(TEMPLATES_PATH) },
            { label: vars.category, to: lp(categoryPath(entry.category)) },
            { label: vars.name },
          ]}
        />
        <div className="mt-10 grid items-center gap-10 lg:grid-cols-2">
          <div>
            <div className="inline-flex rounded-full bg-violet-100 px-3 py-1 font-mono text-xs font-semibold text-violet-700">
              {vars.size}
            </div>
            <h1 className="mt-4 text-4xl font-extrabold tracking-tight text-zinc-900 md:text-5xl">
              {fill(t.h1, vars)}
            </h1>
            <p className="mt-5 text-lg text-zinc-600">{fill(t.lead, vars)}</p>
            <Link
              to={useHref}
              className="mt-8 inline-flex items-center gap-2 rounded-xl bg-brand-600 px-6 py-3 font-semibold text-white shadow-lg shadow-brand-500/25 hover:bg-brand-700"
              data-testid="use-template"
            >
              <Wand2 className="size-4" /> {t.use}
            </Link>
            <p className="mt-3 text-sm text-zinc-500">{t.useHint}</p>
          </div>
          <div className="flex justify-center rounded-3xl bg-gradient-to-br from-indigo-50 via-white to-violet-50 p-6 ring-1 ring-indigo-100">
            <div className="overflow-hidden rounded-xl shadow-xl ring-1 ring-black/5">
              <DocPreview doc={doc} box={440} />
            </div>
          </div>
        </div>

        {sizes.length > 0 && (
          <section className="mt-20">
            <h2 className="text-2xl font-bold tracking-tight text-zinc-900 md:text-3xl">
              {t.sizesTitle}
            </h2>
            <p className="mt-3 max-w-3xl text-zinc-600">{fill(t.sizesText, vars)}</p>
            <div className="mt-8 flex flex-wrap items-end gap-6">
              {sizes.map((d) => (
                <div key={`${d.width}x${d.height}`} className="flex flex-col items-center gap-2">
                  <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black/5">
                    <DocPreview doc={d} box={220} />
                  </div>
                  <span className="font-mono text-xs text-zinc-500">
                    {d.width} × {d.height}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="mt-20 grid gap-10 lg:grid-cols-2">
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-zinc-900">{t.fieldsTitle}</h2>
            <p className="mt-3 text-zinc-600">{t.fieldsText}</p>
            <div className="mt-6 overflow-x-auto rounded-2xl ring-1 ring-zinc-200">
              <table className="w-full text-left text-sm">
                <thead className="bg-zinc-50 text-zinc-900">
                  <tr>
                    {t.fieldHead.map((h) => (
                      <th key={h} scope="col" className="px-4 py-2.5 font-semibold">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100 text-zinc-700">
                  {fields.map((f) => (
                    <tr key={f.name}>
                      <td className="px-4 py-2 font-mono text-xs text-brand-700">{f.name}</td>
                      <td className="px-4 py-2">{t.types[f.type]}</td>
                      <td className="max-w-[16rem] truncate px-4 py-2" title={f.example}>
                        {f.example}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold text-zinc-900">{t.csvTitle}</h2>
              <p className="mt-2 text-sm text-zinc-600">{t.csvText}</p>
              <div className="mt-3">
                <CodeBlock language="csv" code={templateCsv(fields)} />
              </div>
            </div>
            <div>
              <h2 className="text-xl font-bold text-zinc-900">{t.apiTitle}</h2>
              <p className="mt-2 text-sm text-zinc-600">{t.apiText}</p>
              <div className="mt-3">
                <CodeBlock language="POST /v1/renders" code={templateApiBody(fields)} />
              </div>
            </div>
          </div>
        </section>

        {more.length > 0 && (
          <section className="mt-20">
            <h2 className="text-2xl font-bold tracking-tight text-zinc-900">
              {fill(t.moreTitle, vars)}
            </h2>
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {more.map((e) => (
                <TemplateCard key={e.id} entry={e} box={220} />
              ))}
            </div>
            <Link
              to={lp(TEMPLATES_PATH)}
              className="mt-8 inline-flex items-center gap-1 font-semibold text-brand-600 hover:text-brand-700"
            >
              {t.allTemplates} <ArrowRight className="size-4" />
            </Link>
          </section>
        )}
      </section>
    </MarketingShell>
  );
}
